import { useRef } from 'react';

import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import { useMatchMedia } from '@xing-com/crate-hooks-use-match-media';
import { mediaSmallOrTiny } from '@xing-com/layout-tokens';

import { Actions } from './actions';
import { InterrupterCard } from './cards/interrupter-card';
import { useSearchAndShuffleContext, DeckCardContextProvider } from './context';
import { DeckCard } from './deck-card';
import {
  StackContainer,
  ActionsContainer,
  DecksContainer,
} from './decks.styled';
import { CardData } from './types';

type StackProps = {
  index: number;
};

const getCardKey = (
  cardIndex: number,
  cards: CardData[],
  keyMap: React.MutableRefObject<string[]>
): string => {
  const id = cards?.[cardIndex]?.data?.id;
  if (id && !keyMap.current.includes(id)) {
    keyMap.current.push(id);
  }
  const foundIndex = keyMap.current.findIndex((key) => key === id);
  if (foundIndex !== -1) {
    return foundIndex.toString();
  }
  return crypto.randomUUID();
};

export const Stack: React.FC<StackProps> = ({ index }) => {
  const keyMap = useRef<string[]>([]);
  const { decks, loadingStatus } = useSearchAndShuffleContext();
  const { isLoggedOut } = useLoginState();
  const isMobile = useMatchMedia(mediaSmallOrTiny); // be careful: during SSR it will be always true
  const deck = decks[index];
  const interactionCount = deck.loadedCount - deck.cards.length;
  const shouldShowInterrupter =
    isLoggedOut &&
    ((interactionCount > 0 && interactionCount % (isMobile ? 5 : 3) === 0) ||
      deck.cards.length === 0);

  if (shouldShowInterrupter && loadingStatus === 'loaded') {
    return <InterrupterCard />;
  }

  return (
    <StackContainer>
      {deck &&
        [0, 1, 2].map((cardIndex) => (
          <DeckCardContextProvider
            card={deck.cards?.[cardIndex]}
            deckIndex={index}
            key={getCardKey(cardIndex, deck.cards, keyMap)}
          >
            <DeckCard
              deckIndex={index}
              cardIndex={cardIndex}
              key={getCardKey(cardIndex, deck.cards, keyMap)}
            />
          </DeckCardContextProvider>
        ))}

      <ActionsContainer>
        {!shouldShowInterrupter && <Actions index={index} />}
      </ActionsContainer>
    </StackContainer>
  );
};

export const Decks: React.FC = () => {
  const { decks } = useSearchAndShuffleContext();

  return (
    <DecksContainer>
      {decks.map((_, index) => (
        <Stack key={index} index={index} />
      ))}
    </DecksContainer>
  );
};
